const computeWeldedCillStats = records => {
  const uniqueBoxIds = new Set(
    records.map(r => r.boxId ?? r.BoxId).filter(id => id != null)
  );
  const totalFacets = records.reduce(
    (sum, r) => sum + (Number(r.totalFacets ?? r.TotalFacets) || 0),
    0
  );
  return { boxesCreated: uniqueBoxIds.size, totalFacets };
};

const matchPackingStation = (allItems, stationName, stationPath) => {
  const normalizedName = String(stationName || '').trim().toLowerCase();
  const normalizedPath = String(stationPath || '').trim().toLowerCase();
  return (Array.isArray(allItems) ? allItems : []).filter(r => {
    const sn = String(r.stationName || r.StationName || '').trim().toLowerCase();
    const sp = String(r.stationPath || r.StationPath || '').trim().toLowerCase();
    return normalizedPath
      ? sp === normalizedPath || sn === normalizedName
      : sn === normalizedName;
  });
};

export function buildPackingStationView(allItems, stationConfig, metricType = 'weldedCill') {
  const records = matchPackingStation(
    allItems,
    stationConfig.name || '',
    stationConfig.stationPath || ''
  );
  const stats = computeWeldedCillStats(records);

  const groupByKey = (recs, keyFn) =>
    recs.reduce((acc, r) => {
      const k = keyFn(r);
      if (!acc[k]) acc[k] = [];
      acc[k].push(r);
      return acc;
    }, {});

  const toWeldedCillBreakdown = map =>
    Object.entries(map)
      .map(([name, recs]) => {
        const s = computeWeldedCillStats(recs);
        return {
          name,
          value: s.boxesCreated,
          hasWeldedCillData: true,
          weldedCillBoxesCreated: s.boxesCreated,
          weldedCillTotalFacets: s.totalFacets,
          weldedCillFacetsLabel: 'Items'
        };
      })
      .sort((a, b) => b.value - a.value);

  const personKey = r =>
    r.userName || r.operatorName || r.employeeName || r.userFullName ||
    (r.userId ? `User ${r.userId}` : 'Unassigned');

  return {
    name: stationConfig.name,
    stationPath: stationConfig.stationPath || null,
    displayType: (stationConfig.displayType || 'bench').toLowerCase(),
    metricType,
    isResolved: stationConfig.isResolved !== false,
    source: records.length > 0 ? 'api' : 'none',
    records,
    total: stats.boxesCreated,
    hasBoxData: false,
    hasWeldedCillData: true,
    weldedCillBoxesCreated: stats.boxesCreated,
    weldedCillTotalFacets: stats.totalFacets,
    weldedCillFacetsLabel: 'Items',
    shiftBreakdown: toWeldedCillBreakdown(
      groupByKey(records, r => r.shift || r.Shift || 'N/A')
    ),
    peopleBreakdown: toWeldedCillBreakdown(groupByKey(records, personKey))
  };
}

export function buildDirectApiStationView(items, group) {
  const records = Array.isArray(items) ? items : [];

  const toCount = record => {
    const n = Number(record.outputCount ?? record.OutputCount);
    return Number.isFinite(n) ? n : 1;
  };

  const total = records.reduce((sum, r) => sum + toCount(r), 0);

  const shiftMap = {};
  const peopleMap = {};
  records.forEach(r => {
    const shift = r.shift || r.Shift || 'N/A';
    const person =
      r.userName || r.operatorName || r.employeeName || r.userFullName ||
      (r.userId ? `User ${r.userId}` : 'Unassigned');
    const count = toCount(r);
    shiftMap[shift] = (shiftMap[shift] || 0) + count;
    peopleMap[person] = (peopleMap[person] || 0) + count;
  });

  const toBreakdown = map =>
    Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

  return {
    name: group.stations?.[0]?.name || 'Composite Door Gearing',
    stationPath: null,
    displayType: (group.displayType || 'bench').toLowerCase(),
    metricType: 'items',
    isResolved: group.stations?.[0]?.isResolved !== false,
    source: records.length > 0 ? 'api' : 'none',
    records,
    total,
    shiftBreakdown: toBreakdown(shiftMap),
    peopleBreakdown: toBreakdown(peopleMap)
  };
}
